import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { formatDateShort } from "@/lib/format";
import type { CashReceiptsRow } from "@/api/types";
import "./ReportsCashReceipts.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** 30 days back from today, UTC — same rolling-window default every sibling report's own
 *  `defaultRangeStart` uses, duplicated on purpose (that file's own reasoning: each
 *  report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Two decimal places, invariant, NEVER a currency symbol — a farmer reconciles this
 *  column to the cent, so it is never coalesced through `formatMoney`'s own "$" face. */
function amountText(n: number): string {
  return n.toFixed(2);
}

/**
 * Cash receipts journal — Reports' one screen for money received into a cash (Asset-kind)
 * account, tenant-wide, over a window the owner chooses. ef-054-4, ADR 0009 §6 ("Reports
 * is one picker" — a report renders inside the template at `/reports/{slug}`, no area tab
 * strip of its own; "All reports" below is the only way back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsFieldApplications.tsx`'s own remarks give, restated for the identical reason: a
 * journal read against a window nobody asked for is a list a farmer did not request. The
 * From/To inputs still carry a sensible rolling-30-day default so the form is never two
 * empty fields.
 * </p>
 *
 * <p>
 * NO ACCOUNT PICKER, NO ENTITY PICKER — tenant-wide by design (the brief's own scope): the
 * Asset+debit-side filter already narrows every posting to a receipt, and narrowing
 * further to one account would turn "the journal" back into a register page this app
 * already has (`Books.tsx`'s own Entry tab).
 * </p>
 *
 * <p>
 * KEYSET CURSOR, NOT OFFSET — "Load more" appends the next page onto what is already on
 * screen using the server's own opaque `nextCursor`, same reasoning
 * `CashReceiptsReportRepository`'s own remarks give: an offset page cannot stay correct
 * against a farmer who posts a new receipt between two fetches.
 * </p>
 *
 * <p>
 * THE AMOUNT CELL IS BARE, NEVER "$" — `amountText` above, not `formatMoney`: this
 * screen's own numeric face (ADR 0009 §5, `.reports-cash-receipts__amount` in the
 * stylesheet) is reserved for the Amount column's font only, not for a currency-formatted
 * string.
 * </p>
 */
export function ReportsCashReceipts() {
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [entries, setEntries] = useState<CashReceiptsRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const page = await demoApi.getCashReceiptsReport({ from, to });
      setEntries(page.entries);
      setCursor(page.nextCursor);
      setStatus("ready");
    } catch (err) {
      setEntries([]);
      setCursor(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the cash receipts journal."));
    }
  }

  async function loadMore(): Promise<void> {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await demoApi.getCashReceiptsReport({ from, to, cursor });
      setEntries((prev) => [...prev, ...page.entries]);
      setCursor(page.nextCursor);
    } catch (err) {
      setError(problemMessage(err, "Could not load more of the journal."));
    } finally {
      setLoadingMore(false);
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * on-screen page, same reasoning `ReportsFieldApplications.tsx`'s own identical
   * download gives: paying for the whole CSV body on every date change would be wasted
   * work for the visits that never press the button. The server walks every page
   * internally (`CashReceiptsReportRepository.GetAllForExportAsync`), so the file always
   * covers the WHOLE window, never just what "Load more" has fetched so far.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getCashReceiptsCsv({ from, to });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsFieldApplications.tsx`'s own
      // identical download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-cash-receipts-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the cash receipts file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const isEmpty = status === "ready" && entries.length === 0;

  return (
    <main className="reports-cash-receipts">
      <PageHeader
        title="Cash receipts journal"
        subtitle="Money received into a cash account, over a window you choose"
      />

      <a href="/reports" className="reports-cash-receipts__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="reports-cash-receipts__panel"
        aria-labelledby="cash-receipts-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="cash-receipts-heading" className="reports-cash-receipts__heading">
          Journal
        </h2>

        <form className="reports-cash-receipts__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="reports-cash-receipts__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              required
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="reports-cash-receipts__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              required
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary reports-cash-receipts__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="reports-cash-receipts__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the cash receipts journal."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No cash receipts were found."
          />
        )}

        {status === "ready" && entries.length > 0 && (
          <>
            <div
              className="reports-cash-receipts__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="cash-receipts-heading"
            >
              <table className="reports-cash-receipts__table ruled-rows">
                <caption className="reports-cash-receipts__caption">
                  {entries.length} {entries.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Account</th>
                    <th scope="col" className="reports-cash-receipts__num">Amount</th>
                    <th scope="col">Source</th>
                    <th scope="col">Vendor</th>
                    <th scope="col">Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((row) => (
                    <tr key={row.id}>
                      <th scope="row" className="reports-cash-receipts__date">
                        {formatDateShort(row.occurredOn)}
                      </th>
                      <td className="reports-cash-receipts__account">{row.accountName}</td>
                      <td className="reports-cash-receipts__num reports-cash-receipts__amount">
                        {amountText(row.amount)}
                      </td>
                      <td>{row.source}</td>
                      <td>{row.vendorName ?? "—"}</td>
                      <td>{row.memo ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {cursor && (
              <button
                type="button"
                className="btn btn--ghost reports-cash-receipts__load-more"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </>
        )}

        <div className="reports-cash-receipts__export">
          <h3 className="reports-cash-receipts__export-title">Download</h3>
          <p className="reports-cash-receipts__export-copy">
            Every receipt in this window, as a CSV file for a spreadsheet.
          </p>
          <button
            type="button"
            className="btn btn--secondary reports-cash-receipts__export-btn"
            disabled={!canSubmit || csvState.kind === "busy"}
            onClick={() => void downloadCsv()}
          >
            {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
          </button>
          {csvState.kind === "done" && (
            <p className="reports-cash-receipts__export-result" role="status">
              Downloaded {csvState.rows} {csvState.rows === 1 ? "row" : "rows"}.
            </p>
          )}
          {csvState.kind === "error" && (
            <p className="reports-cash-receipts__export-error" role="alert">
              {csvState.message}
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
